import { type NotificationData, notifications } from "@mantine/notifications";

import { ErrorIcon, InfoIcon, SuccessIcon } from "../toast/icons";
import classes from "./GlobalNotification.module.css";

export interface NotificationId {
    id: string;
}

type NotificationType = "success" | "error" | "info";

export type GlobalNotificationOptions = Omit<NotificationData, "color" | "icon" | "message"> & {
    persistent?: boolean;
    delay?: number;
    position?: NotificationData["position"];
};

const DEFAULT_DELAY = 10_000;

const iconMap = {
    success: <SuccessIcon />,
    error: <ErrorIcon />,
    info: <InfoIcon />,
};

const colorMap = {
    success: "green.6",
    error: "red.8",
    info: "dark.9",
};

const DEFAULT_POSITION = "bottom-right";

function show(type: NotificationType, message: string, opts: GlobalNotificationOptions = {}): NotificationId {
    const { persistent, delay, autoClose, position = DEFAULT_POSITION, ...rest } = opts;

    const id = notifications.show({
        message,
        color: colorMap[type],
        icon: iconMap[type],
        autoClose: persistent ? false : (autoClose ?? delay ?? DEFAULT_DELAY),
        withCloseButton: true,
        position,
        classNames: {
            root: classes.root,
            body: classes.body,
            icon: classes.icon,
            description: classes.description,
            title: classes.title,
            closeButton: classes.closeButton,
        },
        ...rest,
    });
    return { id };
}

export const useNotification = () => ({
    info: (message: string, opts?: GlobalNotificationOptions) => show("info", message, opts),
    success: (message: string, opts?: GlobalNotificationOptions) => show("success", message, opts),
    error: (message: string, opts?: GlobalNotificationOptions) => show("error", message, opts),
    hide: (notification: NotificationId) => notifications.hide(notification.id),
});
